import { useState } from 'react';
import PropTypes from 'prop-types';
import { readerApi } from '../../services/api';
import './CreateReaderForm.css';

const CreateReaderForm = ({ onReaderCreated, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    age: '',
    gender: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Reader name is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const submitData = {
        name: formData.name.trim(),
      };

      // Only include age if provided and valid
      if (formData.age && parseInt(formData.age) > 0) {
        submitData.age = parseInt(formData.age);
      }

      // Only include gender if selected
      if (formData.gender) {
        submitData.gender = formData.gender;
      }
      
      const response = await readerApi.createReader(submitData);
      onReaderCreated(response.data.reader);
    } catch (err) {
      console.error('Error creating reader:', err);
      setError(err.response?.data?.error || 'Failed to create reader');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create-reader-form">
      <div className="form-header">
        <h3>Add a reader</h3>
        <p>Add a family member or someone else you want to track reading for.</p>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="name">
            Name *
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            placeholder="Enter reader's name"
            maxLength={100}
            required
            disabled={loading}
          />
          <small className="form-text">
            The name of the person you're adding as a reader.
          </small>
        </div>

        <div className="form-group">
          <label htmlFor="age">
            Age (optional)
          </label>
          <input
            type="number"
            id="age"
            name="age"
            value={formData.age}
            onChange={handleInputChange}
            placeholder="Enter age"
            min="1"
            max="150"
            disabled={loading}
          />
          <small className="form-text">
            Age in years (optional).
          </small>
        </div>

        <div className="form-group">
          <label htmlFor="gender">
            Gender (optional)
          </label>
          <select
            id="gender"
            name="gender"
            value={formData.gender}
            onChange={handleInputChange}
            disabled={loading}
          >
            <option value="">Select gender</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
            <option value="prefer_not_to_say">Prefer not to say</option>
          </select>
          <small className="form-text">
            Gender identification (optional).
          </small>
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading || !formData.name.trim()}
          >
            {loading ? 'Adding...' : 'Add reader'}
          </button>
        </div>
      </form>

      <div className="form-info">
        <h4>About readers</h4>
        <ul>
          <li>You can add family members, children, or anyone you want to track reading for</li>
          <li>Age and gender information is optional but helps with logging</li>
          <li>You can edit reader information later if needed</li>
          <li>Readers can be added to families to share reading tracking</li>
        </ul>
      </div>
    </div>
  );
};

CreateReaderForm.propTypes = {
  onReaderCreated: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default CreateReaderForm;
